import styled from 'styled-components';
import {
  mq,
  getSpace,
  getTextType,
  getColor,
  gridContainer,
  setGridColumns
} from '../../../stylesheets/tools';

const easing = 'cubic-bezier(.48,0,.5,1)';

const StyledNavigation = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;

  [data-whatinput='mouse'] &,
  [data-whatinput='touch'] & {
    outline: 0;
  }

  @keyframes itemSlideIn {
    from {
      transform: translateX(40px);
      opacity: 0;
    }

    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  .navigation__content-wrapper {
    ul {
      list-style-type: none;
      margin: 0;
      padding: 0;
    }
  }

  .navigation__content-wrapper {
    transition: all 0.4s ${easing};
    padding-top: ${getSpace('120px')};

    ${mq('m')} {
      padding-top: ${getSpace('150px')};
    }

    display: block;
    width: 100%;
    height: 100%;
    min-height: 100vh;
  }

  // GENERAL STYLING

  div.navigation__content-wrapper {
    // All items
    li.navigation__main-item,
    li.navigation__main-item button,
    .navigation__sub-token-headline,
    .navigation__sub-grid-col button.navigation__item-link {
      color: ${getColor('white')};
      text-align: left;
    }

    // Headline Size
    li.navigation__main-item > a,
    li.navigation__main-item button {
      font-size: 24px;
      line-height: 1.25;

      ${mq('m')} {
        font-size: 30px;
      }
    }

    // Headline Style
    .navigation__main-item > a,
    .navigation__main-item button,
    .navigation__sub-token-headline,
    .navigation__sub-grid-col li a:not(.navigation__item-child-link),
    .navigation__sub-grid-col button.navigation__item-link {
      ${getTextType('subline')};
      text-transform: uppercase;
    }

    .navigation__main-item .navigation__item-child {
      padding: 15px 0 10px 15px;
      ${getTextType('copy', 'international')};

      ${mq('m')} {
        padding: 20px 0 15px 20px;
      }

      li {
        margin-bottom: 8px;

        ${mq('m')} {
          margin-bottom: 10px;
        }

        a {
          font-size: 18px;

          ${mq('m')} {
            font-size: 20px;
          }
        }
      }
    }
  }

  button.navigation__item-link {
    display: flex;
    align-items: center;
    gap: 12px;

    svg {
      transition: transform 0.2s ease;
    }
    &.is--open {
      svg {
        transform: rotate(180deg);
      }
    }
  }

  // MAIN NAVIGATION

  .navigation__main-container {
    ${gridContainer()}
    ${setGridColumns('1fr', 1)}
    height: 100%;
    gap: 25px;

    ${mq('m')} {
      gap: 30px;
      ${setGridColumns('1fr', 2)}
    }

    ${mq('xl')} {
      ${setGridColumns('1fr', 3)}
    }
  }

  // SUB NAVIGATION
  .navigation__sub-container {
    padding-top: 80px;

    .navigation__sub-headline {
      color: ${getColor('darkGray')};
      margin-bottom: 15px;

      p {
        margin: 0;
        font-size: 12px;
      }

      ${mq('m')} {
        margin-bottom: 20px;

        p {
          font-size: 16px;
        }
      }
    }
    .navigation__sub-grid {
      ${gridContainer()}
      ${setGridColumns('1fr', 1)}
      gap: 60px 30px;

      ${mq('m')} {
        gap: 60px 30px;
        ${setGridColumns('1fr', 2)}
      }

      ${mq('xl')} {
        ${setGridColumns('1fr', 3)}
      }

      .navigation__sub-token {
        .navigation__sub-token-headline {
          margin-bottom: 15px;
          font-size: 16px;
          line-height: 1.25;

          ${mq('m')} {
            font-size: 18px;
            margin-bottom: 20px;
          }
        }
        .navigation__sub-token-copy {
          margin-top: 20px;
          color: ${getColor('teaserGray')};

          p {
            font-size: 12px;
            line-height: 1.5;
          }

          ${mq('m')} {
            margin-top: 30px;
          }
        }
      }
      .navigation__sub-grid-col {
        color: white;

        > li {
          margin-bottom: 15px;
        }
        .navigation__item-link {
          font-size: 16px;

          svg {
            height: 16px;
            width: 16px;
          }
          ${mq('m')} {
            font-size: 18px;

            svg {
              height: 18px;
              width: 18px;
            }
          }
        }
        .navigation__item-child {
          padding: 10px 0 5px 10px;

          > li {
            margin-bottom: 8px;
          }

          ${mq('m')} {
            padding: 15px 0 10px 15px;
          }
        }
      }
    }
  }

  /* Navigation wrapper */
  nav {
    ${mq('l')} {
      flex: 1 1 100%;
      margin-right: ${getSpace('s')};
    }
  }

  .navigation__meta {
    margin: ${getSpace('xxxl')} ${getSpace('m')};
    max-width: 400px;
    min-height: calc(100vh - (${getSpace('xxxl')} * 2));
    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    ${mq('m')} {
      width: 100%;
    }

    ${mq('xl')} {
      margin-right: ${getSpace('150px')};
    }

    ${mq('xxl')} {
      max-width: 500px;
    }
  }

  /* Current Info */
  .navigation__currentInfo,
  .navigation__loginMask {
    transition: all 0.4s ${easing};
    transform: translateX(40px);
    opacity: 0;
    animation: itemSlideIn 0.2s ${easing} forwards;
    display: ${props => (props.currentLevel > 1 ? 'none' : 'block')};

    &:active {
      color: ${getColor('white')};
    }

    ${mq('l')} {
      align-self: flex-end;
    }

    &.exiting {
      opacity: 0;
    }

    &.entering,
    &.exited {
      display: none;
    }
  }

  /* Close button */
  .navigation__close {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    z-index: 4;

    svg {
      position: absolute;
      bottom: 75px;
      right: -12px;
      fill: #fff;
      width: 29px;
      height: 18px;
    }

    [data-whatinput='mouse'] &,
    [data-whatinput='touch'] & {
      outline: 0;
    }
  }
`;

export default StyledNavigation;
